import { Check } from "lucide-react";
import { Reveal } from "./Reveal";

const PILLARS = [
  {
    num: "01",
    title: "Fixation d'objectifs",
    desc: "Un jeune sans cap ressemble à un combattant sans plan de combat. Définir ensemble un objectif clair, le découper en étapes mesurables et célébrer chaque progression.",
    takeaway: "Un cap concret, visible et motivant",
  },
  {
    num: "02",
    title: "Routines & discipline",
    desc: "La motivation passe, les routines restent. Installation d'habitudes simples — sommeil, entraînement, travail scolaire ou études — qui structurent la semaine et redressent la posture.",
    takeaway: "Sortie des écrans & énergie constructive",
  },
  {
    num: "03",
    title: "Gestion des pensées & rebond",
    desc: "Repérer le dialogue intérieur négatif, respirer sous la pression et traiter l'échec comme une information, jamais comme une fatalité.",
    takeaway: "Stress maîtrisé, échecs transformés en tremplins",
  },
];

const AUDIENCE = [
  "Étudiants (collège, lycée, études supérieures) en quête d'un cap clair : méthode, stress des évaluations, orientation",
  "Sportifs et compétiteurs qui veulent gagner en précision, en concentration et en confiance le jour J",
  "Adultes et jeunes adultes en transition : projet professionnel, reconversion, équilibre de vie",
  "Parents qui souhaitent offrir à leur enfant un cadre structurant, bienveillant et valorisant",
];

const Methode = () => (
  <section id="methode" className="relative py-24 sm:py-32" data-testid="methode-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ember mb-4">Ma méthode</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100 max-w-3xl leading-tight">
          « Combat & Cap » : du tatami au quotidien
        </h2>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Une préparation mentale adaptée aux objectifs de chaque apprenant.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.12}>
            <div
              data-testid={`method-card-${i}`}
              className="group h-full rounded-2xl border border-white/10 bg-coal p-7 transition-all duration-300 hover:border-ember/40 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(230,57,70,0.25)]"
            >
              <span className="font-heading text-6xl font-black text-outline leading-none">{p.num}</span>
              <h3 className="mt-5 font-heading text-xl sm:text-2xl font-bold uppercase tracking-normal text-slate-100 leading-snug">
                {p.title}
              </h3>
              <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">{p.desc}</p>
              <div className="mt-6 inline-flex items-center rounded-full bg-gold/10 border border-gold/25 px-3.5 py-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-amber-300">{p.takeaway}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 grid gap-10 lg:grid-cols-2 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl border border-ember/25 translate-x-3 translate-y-3" />
            <img
              src="/images/laureat-ring.png"
              alt="Jeune diplômé de dos, toge et chapeau de lauréat, parchemin à la main et gants de boxe sur l'épaule, face à la lumière dans la salle d'entraînement"
              data-testid="methode-diploma-img"
              className="relative rounded-2xl w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
            <span className="absolute bottom-4 left-4 rounded-full bg-obsidian/90 backdrop-blur border border-white/15 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
              Tatami × Réussite scolaire
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase text-slate-100">
            À qui s'adresse cet accompagnement ?
          </h3>
          <ul className="mt-7 space-y-5">
            {AUDIENCE.map((a) => (
              <li key={a} className="flex items-start gap-3.5">
                <span className="mt-0.5 flex items-center justify-center w-6 h-6 rounded-full bg-ember/15 border border-ember/30 shrink-0">
                  <Check size={13} className="text-ember" />
                </span>
                <span className="text-sm sm:text-base text-slate-300 leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
          <a
            href="/test"
            data-testid="methode-test-cta"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-gold text-obsidian px-7 py-3.5 text-xs font-bold uppercase tracking-wide hover:opacity-90 transition-opacity"
          >
            Faire le test gratuit (2 min)
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Methode;
